import React, { useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Popconfirm,
  Typography,
  Tag,
  Spin,
  Switch
} from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { forceUpdatesApi } from '../api/forceUpdates';
import { appsApi } from '../api/apps';
import { ForceUpdate, CreateForceUpdateRequest, UpdateForceUpdateRequest, App } from '../types';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface ForceUpdatesProps {
  selectedAppId?: number;
}

const ForceUpdates: React.FC<ForceUpdatesProps> = ({ selectedAppId }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingForceUpdate, setEditingForceUpdate] = useState<ForceUpdate | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const { data: forceUpdatesResponse, isLoading } = useQuery({
    queryKey: ['force-updates', selectedAppId],
    queryFn: () => forceUpdatesApi.getForceUpdates(1, 1000, selectedAppId),
    refetchOnMount: true,
    staleTime: 0, // Data luôn được coi là stale, sẽ refetch mỗi lần
  });

  const { data: appsResponse } = useQuery({
    queryKey: ['apps'],
    queryFn: () => appsApi.getApps(),
    enabled: isModalVisible, // Chỉ fetch khi modal mở
  });

  const createMutation = useMutation({
    mutationFn: forceUpdatesApi.createForceUpdate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['force-updates'] });
      message.success('Tạo force update thành công!');
      setIsModalVisible(false);
      form.resetFields();
    },
    onError: (error: any) => {
      console.error('Create force update error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
      alert('❌ Lỗi khi tạo force update: ' + errorMessage);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ appId, version, data }: { appId: number; version: string; data: UpdateForceUpdateRequest }) => {
      console.log('updateMutation.mutationFn called with:', { appId, version, data });
      const result = await forceUpdatesApi.updateForceUpdate(appId, version, data);
      console.log('updateForceUpdate API call result:', result);
      return result;
    },
    onSuccess: (data) => {
      console.log('Update force update success:', data);
      queryClient.invalidateQueries({ queryKey: ['force-updates'] });
      message.success('Cập nhật force update thành công!');
      setIsModalVisible(false);
      setEditingForceUpdate(null);
      form.resetFields();
    },
    onError: (error: any) => {
      console.error('Update force update error:', error);
      console.error('Error details:', error?.response?.data);
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
      alert('❌ Lỗi khi cập nhật force update: ' + errorMessage);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ appId, version }: { appId: number; version: string }) =>
      forceUpdatesApi.deleteForceUpdate(appId, version),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['force-updates'] });
      message.success('Xóa force update thành công!');
    },
    onError: (error: any) => {
      console.error('Delete force update error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
      alert('❌ Lỗi khi xóa force update: ' + errorMessage);
    },
  });

  const handleCreate = () => {
    setEditingForceUpdate(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (forceUpdate: ForceUpdate) => {
    console.log('Editing force update:', forceUpdate);
    setEditingForceUpdate(forceUpdate);
    form.setFieldsValue({
      update_reason: forceUpdate.update_reason,
      is_active: forceUpdate.is_active,
    });
    console.log('Form values set:', {
      update_reason: forceUpdate.update_reason,
      is_active: forceUpdate.is_active,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (record: ForceUpdate) => {
    deleteMutation.mutate({ appId: record.app_id, version: record.version });
  };

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      console.log('Form values:', values);
      console.log('is_active value:', values.is_active, 'type:', typeof values.is_active);
      if (editingForceUpdate) {
        console.log('Updating force update:', {
          appId: editingForceUpdate.app_id,
          version: editingForceUpdate.version,
          data: {
            update_reason: values.update_reason,
            is_active: values.is_active,
          }
        });
        updateMutation.mutate({
          appId: editingForceUpdate.app_id,
          version: editingForceUpdate.version,
          data: {
            update_reason: values.update_reason,
            is_active: values.is_active,
          }
        });
      } else {
        createMutation.mutate(values as CreateForceUpdateRequest);
      }
    });
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingForceUpdate(null);
    form.resetFields();
  };

  const getAppName = (appId: number) => {
    return appsResponse?.find((app: App) => app.id === appId)?.name || appId.toString();
  };

  // Data is already filtered by backend
  const forceUpdates = forceUpdatesResponse?.data || [];

  const columns = [
    {
      title: 'App',
      dataIndex: 'app_id',
      key: 'app_id',
      render: (appId: number) => getAppName(appId),
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
    },
    {
      title: 'Lý do',
      dataIndex: 'update_reason',
      key: 'update_reason',
      render: (reason: string) => (
        <div style={{ 
          whiteSpace: 'pre-wrap', 
          wordBreak: 'break-word',
          maxWidth: '250px'
        }}>
          {reason}
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString('vi-VN'),
    },
    {
      title: 'Cập nhật lần cuối',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date: string) => new Date(date).toLocaleString('vi-VN'),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      render: (_: any, record: ForceUpdate) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Sửa
          </Button>
          <Popconfirm
            title="Bạn có chắc muốn xóa force update này?"
            onConfirm={() => handleDelete(record)}
            okText="Có"
            cancelText="Không"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Xóa
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '300px',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <Spin size="large" />
        <span style={{ color: '#666', fontSize: '16px' }}>Đang tải dữ liệu force updates...</span>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2}>Force Updates</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          Thêm Force Update
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={forceUpdates}
        loading={isLoading}
        rowKey={(record) => `${record.app_id}-${record.version}`}
        pagination={{
          total: forceUpdatesResponse?.total || 0,
          pageSize: forceUpdatesResponse?.page_size || 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} của ${total} force updates`,
        }}
      />

      <Modal
        title={editingForceUpdate ? "Sửa Force Update" : "Thêm Force Update mới"}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
      >
        <Form
          form={form}
          layout="vertical"
        >
          {!editingForceUpdate && (
            <>
              <Form.Item
                name="app_id"
                label="App"
                rules={[{ required: true, message: 'Vui lòng chọn app!' }]}
              >
                <Select placeholder="Chọn app">
                  {appsResponse?.map((app: App) => (
                    <Option key={app.id} value={app.id}>
                      {app.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="version"
                label="Version"
                rules={[{ required: true, message: 'Vui lòng nhập version!' }]}
              >
                <Input placeholder="1.0.0" />
              </Form.Item>
            </>
          )}

          <Form.Item
            name="update_reason"
            label="Lý do force update"
          >
            <TextArea
              placeholder="Mô tả lý do cần force update"
              rows={4}
            />
          </Form.Item>

          {editingForceUpdate && (
            <Form.Item
              name="is_active"
              label="Trạng thái"
              valuePropName="checked"
              getValueFromEvent={(checked) => {
                console.log('Switch changed to:', checked);
                return checked;
              }}
            >
              <Switch 
                checkedChildren="Active" 
                unCheckedChildren="Inactive"
                defaultChecked={editingForceUpdate.is_active}
                onChange={(checked) => {
                  console.log('Switch onChange:', checked);
                  form.setFieldValue('is_active', checked);
                }}
              />
              <div style={{ marginTop: 4, fontSize: '12px', color: '#666' }}>
                Hiện tại: {editingForceUpdate.is_active ? 'Active (Force update đang được áp dụng)' : 'Inactive (Force update không được áp dụng)'}
              </div>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default ForceUpdates;
